/**
 * WindowSync — BroadcastChannel management for cross-window font syncing.
 *
 * Each editor window creates a WindowSync, which bridges the local
 * PatchSyncEngine ↔ BroadcastChannel. When a new window opens it requests
 * the full Y.Doc state; existing windows respond.
 */
import { Logger } from './logger';
const console = new Logger('WindowSync');
// ── WindowSync class ────────────────────────────────────────────────
export class WindowSync {
    static enableTimingLogging() {
        WindowSync._timingLoggingEnabled = true;
        console.log('Timing logging enabled');
    }
    static disableTimingLogging() {
        WindowSync._timingLoggingEnabled = false;
        console.log('Timing logging disabled');
    }
    constructor(bridge, channelName) {
        this._channel = null;
        this._peers = new Set();
        this._awaitingFullState = false;
        this._hasAppliedFullState = false;
        this._fullStateBootstrapResolve = null;
        this._fullStateBootstrapReject = null;
        this._fullStateBootstrapTimeout = null;
        this._mainWindowClosingListeners = new Set();
        this._pendingOutboundPackets = [];
        this._outboundFlushScheduled = false;
        this._pendingYjsMessages = [];
        this._inboundFlushScheduled = false;
        this._bridge = bridge;
        this._sessionId = window.windowRole?.sessionId ?? 'main';
        if (typeof BroadcastChannel !== 'undefined') {
            this._channel = new BroadcastChannel(channelName);
            this._channel.onmessage = (ev) => {
                this._handleMessage(ev.data);
            };
            // Wire bridge's local updates to broadcast. The broadcast itself is
            // microtask-batched so a single user transaction that emits several
            // Yjs updates produces one channel message and one receiver refresh.
            bridge.onLocalUpdate((_update, collaborationMessage) => {
                this._queueOutboundBroadcast(_update, collaborationMessage);
            });
        }
    }
    /**
     * Request the full state from an existing peer window.
     * Call this when a new window opens with `sync=true`.
     *
     * Pre-registers a pending worker-document sync promise with
     * FontCompilation so that any editing compile that fires before the
     * full-state-response arrives waits for the linked-window bootstrap
     * instead of failing with "requires a ready worker Yjs document".
     * (Compilation edit policy rule 24: cached editing compiles MUST wait
     * for the tracked worker-document sync whenever the gate is closed.)
     */
    requestFullState() {
        this._awaitingFullState = true;
        this._hasAppliedFullState = false;
        const fontCompilation = window.fontCompilation;
        if (fontCompilation && !this._fullStateBootstrapResolve) {
            fontCompilation.setWorkerCacheDocumentReady?.(false);
            const bootstrapPromise = new Promise((resolve, reject) => {
                this._fullStateBootstrapResolve = resolve;
                this._fullStateBootstrapReject = reject;
            });
            // Track this as a pending worker-document sync so
            // awaitWorkerDocumentSync() in compileEditingFromJsonCached
            // blocks until the linked-window seedYdoc completes.
            // The ready gate is closed above before the request is sent, so
            // early compiles always wait for this pending bootstrap.
            fontCompilation.trackWorkerDocumentSync(bootstrapPromise);
            // Safety timeouts: if the main window never responds (closed,
            // crashed, or on a different font), reject the bootstrap promise
            // so compiles don't hang forever. workerCacheDocumentReady is
            // already false, so the compile will fail after
            // awaitWorkerDocumentSync rejects.
            this._fullStateBootstrapTimeout = setTimeout(() => {
                this._resolveFullStateBootstrap(new Error('Linked-window full-state-response timed out — no peer window responded within 15s'));
            }, 15000);
        }
        this._send({
            type: 'full-state-request',
            windowId: this._bridge.windowId,
            sessionId: this._sessionId
        });
    }
    /** Announce that this window is closing. */
    announceClose() {
        this._send({
            type: 'window-closing',
            windowId: this._bridge.windowId,
            sessionId: this._sessionId
        });
    }
    /**
     * Resolve or reject the pending full-state bootstrap promise.
     * Pass null to resolve (bootstrap succeeded), or an Error to reject.
     * Clears the timeout and nulls the resolver/rejector so subsequent
     * calls are no-ops.
     */
    _resolveFullStateBootstrap(error) {
        if (this._fullStateBootstrapTimeout) {
            clearTimeout(this._fullStateBootstrapTimeout);
            this._fullStateBootstrapTimeout = null;
        }
        const resolve = this._fullStateBootstrapResolve;
        const reject = this._fullStateBootstrapReject;
        this._fullStateBootstrapResolve = null;
        this._fullStateBootstrapReject = null;
        if (error) {
            reject?.(error);
        }
        else {
            resolve?.();
        }
        if (this._pendingYjsMessages.length > 0 &&
            !this._inboundFlushScheduled) {
            this._inboundFlushScheduled = true;
            queueMicrotask(() => this._flushPendingYjsUpdates());
        }
    }
    announceMainWindowClosing() {
        this._send({
            type: 'main-window-closing',
            windowId: this._bridge.windowId,
            sessionId: this._sessionId
        });
    }
    broadcastCloudRelayUpdate(update, collaborationMessage) {
        this._sendYjsUpdate([
            {
                update,
                ...(collaborationMessage ? { collaborationMessage } : undefined)
            }
        ]);
    }
    broadcastCloudConnectionStatus(state) {
        this._send({
            type: 'cloud-connection-status',
            state,
            windowId: this._bridge.windowId,
            sessionId: this._sessionId
        });
    }
    onMainWindowClosing(callback) {
        this._mainWindowClosingListeners.add(callback);
        return () => {
            this._mainWindowClosingListeners.delete(callback);
        };
    }
    /** Get the set of known peer window IDs. */
    get peers() {
        return this._peers;
    }
    /** Clean up. */
    destroy() {
        this._flushOutboundBroadcast();
        this.announceClose();
        this._channel?.close();
        this._channel = null;
    }
    // ── Internal ─────────────────────────────────────────────────
    _send(msg) {
        try {
            this._channel?.postMessage(msg);
        }
        catch {
            // Channel may be closed
        }
    }
    _queueOutboundBroadcast(update, collaborationMessage) {
        this._pendingOutboundPackets.push({
            update,
            ...(collaborationMessage ? { collaborationMessage } : undefined)
        });
        if (this._outboundFlushScheduled) {
            return;
        }
        this._outboundFlushScheduled = true;
        queueMicrotask(() => this._flushOutboundBroadcast());
    }
    _flushOutboundBroadcast() {
        if (!this._outboundFlushScheduled) {
            return;
        }
        this._outboundFlushScheduled = false;
        const packets = this._pendingOutboundPackets;
        this._pendingOutboundPackets = [];
        if (!packets.length) {
            return;
        }
        const startTime = performance.now?.() ?? Date.now();
        this._sendYjsUpdate(packets);
        this._logTiming('outbound-yjs-update', {
            updateCount: packets.length,
            collaborationMessageCount: packets.filter((packet) => !!packet.collaborationMessage).length,
            updateBytes: packets.reduce((total, packet) => total + toUint8Array(packet.update).byteLength, 0),
            peerCount: this._peers.size,
            durationMs: this._elapsed(startTime)
        });
    }
    _sendYjsUpdate(packets) {
        this._send({
            type: 'yjs-update',
            updates: packets,
            windowId: this._bridge.windowId,
            sessionId: this._sessionId
        });
    }
    _queueYjsUpdate(msg) {
        this._pendingYjsMessages.push(msg);
        if (this._fullStateBootstrapResolve) {
            return;
        }
        if (this._inboundFlushScheduled) {
            return;
        }
        this._inboundFlushScheduled = true;
        queueMicrotask(() => this._flushPendingYjsUpdates());
    }
    _flushPendingYjsUpdates() {
        if (!this._inboundFlushScheduled) {
            return;
        }
        this._inboundFlushScheduled = false;
        if (this._fullStateBootstrapResolve) {
            return;
        }
        const messages = this._pendingYjsMessages;
        this._pendingYjsMessages = [];
        if (!messages.length) {
            return;
        }
        const startTime = performance.now?.() ?? Date.now();
        let updateBytes = 0;
        let collaborationMessageCount = 0;
        for (const msg of messages) {
            for (const packet of msg.updates) {
                const update = toUint8Array(packet.update);
                updateBytes += update.byteLength;
                if (packet.collaborationMessage) {
                    collaborationMessageCount += 1;
                }
                try {
                    this._bridge.applyRemoteUpdate(update, undefined, packet.collaborationMessage
                        ? [packet.collaborationMessage]
                        : undefined);
                    if (window.windowRole?.isMainWindow()) {
                        window.cloudPlugin?.relayPeerWindowUpdateToCloud?.(update, packet.collaborationMessage ?? null);
                    }
                }
                catch (error) {
                    console.warn('WindowSync: failed to apply inbound Yjs update:', error);
                }
            }
        }
        this._logTiming('inbound-yjs-update', {
            messageCount: messages.length,
            collaborationMessageCount,
            updateBytes,
            durationMs: this._elapsed(startTime)
        });
    }
    _elapsed(startTime) {
        const now = performance.now?.() ?? Date.now();
        return Math.round((now - startTime) * 10) / 10;
    }
    _logTiming(label, detail) {
        if (!WindowSync._timingLoggingEnabled) {
            return;
        }
        console.log(label, detail);
    }
    _handleMessage(msg) {
        if (msg.sessionId !== this._sessionId) {
            return;
        }
        switch (msg.type) {
            case 'yjs-update':
                if (msg.windowId === this._bridge.windowId)
                    return;
                this._peers.add(msg.windowId);
                this._queueYjsUpdate(msg);
                break;
            case 'full-state-request':
                if (msg.windowId === this._bridge.windowId)
                    return;
                this._peers.add(msg.windowId);
                // Respond with our full state
                const state = this._bridge.getFullState();
                this._send({
                    type: 'full-state-response',
                    state,
                    changeLog: this._bridge.getChangeLog(),
                    collaborationLog: this._bridge.getCollaborationLog(),
                    cloudRelayState: window.windowRole?.isMainWindow() &&
                        window.cloudPlugin?.getRelayConnectionState
                        ? window.cloudPlugin.getRelayConnectionState()
                        : undefined,
                    windowId: this._bridge.windowId,
                    sessionId: this._sessionId
                });
                break;
            case 'full-state-response':
                if (msg.windowId === this._bridge.windowId)
                    return;
                this._peers.add(msg.windowId);
                if (!this._awaitingFullState || this._hasAppliedFullState) {
                    return;
                }
                this._hasAppliedFullState = true;
                this._awaitingFullState = false;
                const fullState = toUint8Array(msg.state);
                // Import change log before applying state so the
                // onRemoteChange callback (fired by applyFullState)
                // sees the complete log.
                this._bridge.importChangeLog(msg.changeLog);
                this._bridge.importCollaborationMessages(msg.collaborationLog ?? []);
                this._bridge.applyFullState(fullState);
                const fontCompilation = window.fontCompilation;
                if (fontCompilation) {
                    const fontManager = window.fontManager;
                    void (async () => {
                        const initialized = fontCompilation.isInitialized
                            ? true
                            : await fontCompilation.initialize();
                        if (!initialized) {
                            throw new Error('Font compilation worker not initialized for linked-window bootstrap');
                        }
                        if (!fontManager?.currentFont) {
                            throw new Error('No font loaded for linked-window worker bootstrap');
                        }
                        // Build a worker seed state (array-format nodes).
                        // Rust now accepts array nodes natively via the updated serde.
                        // YJS_ONLY (N2): Binary Yjs full-state-response —
                        // no JSON crossing. The bridge.getFullState() call at line ~343
                        // produces binary Yjs state.
                        const seedState = fontManager.buildWorkerSeedYjsState?.();
                        if (!seedState?.length) {
                            throw new Error('Failed to build worker seed Yjs state for linked-window bootstrap');
                        }
                        fontManager.recordFullFontCrossing?.();
                        // YJS_ONLY (N3): Binary Yjs seed for the worker
                        // Y.Doc — the CRDT baseline, not a JSON crossing.
                        // seedYdoc (init_ydoc_from_state) populates all Rust
                        // caches from the binary Yjs state, so no storeFontJson
                        // is needed. No second JS worker-mirror Y.Doc.
                        await fontCompilation.sendMessage({
                            type: 'seedYdoc',
                            state: seedState
                        });
                    })()
                        .then(() => {
                        this._resolveFullStateBootstrap(null);
                    })
                        .catch((error) => {
                        console.warn('Failed to bootstrap worker state from full-state response', error);
                        window.fontCompilation?.setWorkerCacheDocumentReady?.(false);
                        this._resolveFullStateBootstrap(error);
                    });
                }
                else {
                    // fontCompilation not available — resolve the bootstrap
                    // so compiles don't hang; they'll fail with their own
                    // initialization error instead.
                    this._resolveFullStateBootstrap(null);
                }
                if (msg.cloudRelayState) {
                    window.cloudPlugin?.applyRelayedConnectionState?.(msg.cloudRelayState);
                }
                break;
            case 'window-closing':
                this._peers.delete(msg.windowId);
                break;
            case 'main-window-closing':
                if (msg.windowId === this._bridge.windowId)
                    return;
                for (const callback of this._mainWindowClosingListeners) {
                    callback();
                }
                break;
            case 'cloud-connection-status':
                if (msg.windowId === this._bridge.windowId)
                    return;
                window.cloudPlugin?.applyRelayedConnectionState?.(msg.state);
                break;
        }
    }
}
WindowSync._timingLoggingEnabled = false;
function toUint8Array(payload) {
    if (payload instanceof Uint8Array) {
        return payload;
    }
    if (Array.isArray(payload)) {
        return new Uint8Array(payload);
    }
    return new Uint8Array(payload);
}
